import { Actions, createEffect, ofType } from '@ngrx/effects'
import { filter, map, switchMap, withLatestFrom } from 'rxjs'

import { Injectable } from '@angular/core'
import { ChangeRequestActions } from '../actions/change-request.actions'
import { ChangeItemType, ChangeType } from '../../model'
import { ScopeOverviewSelectors } from '@app/features/scope-overview/store/selectors/scope-overview.selector'
import { ChangeRequestSelectors } from '../selectors/change-request.selectors'
import { Store } from '@ngrx/store'
import { getRawChanges } from '../../util/entity-changes'

@Injectable()
export class ChangeRequestComponentEffects {
  constructor(
    private actions$: Actions,
    private store$: Store
  ) {}

  modifyComponent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.modifyComponent),
      withLatestFrom(this.store$.select(ChangeRequestSelectors.selectChangeItemEntities)),
      filter(([_, currentScope]) => !!currentScope),
      map(([{ identity, parentId, changes }, changeItemEntities]) => {
        const parentChangeItem = changeItemEntities?.[parentId]

        if (parentChangeItem && parentChangeItem.changeType === ChangeType.ADDED) {
          const deliverable = parentChangeItem.data?.data || parentChangeItem.data
          const components = deliverable?.components?.map((component) =>
            component.id === identity ? { ...component, ...getRawChanges(changes) } : component
          )

          return ChangeRequestActions.modifyDeliverable({
            identity: deliverable?.changeAuditData?.identity || deliverable.id,
            changes: { components: { new: components } },
          })
        }

        return ChangeRequestActions.applyChanges({
          payload: {
            changeType: ChangeType.MODIFIED,
            type: ChangeItemType.COMPONENT,
            identity,
            parentId,
            data: changes,
          },
        })
      })
    )
  )

  deleteComponent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.deleteComponent),
      withLatestFrom(
        this.store$.select(ChangeRequestSelectors.selectChangeItemEntities),
        this.store$.select(ScopeOverviewSelectors.selectCurrentScope),
        this.store$.select(ChangeRequestSelectors.selectCurrentChangeRequest)
      ),
      switchMap(([{ componentId, deliverableId }, changeItemEntities, currentScope, currentChangeRequest]) => {
        const parentChangeItem = changeItemEntities?.[deliverableId]

        if (parentChangeItem && parentChangeItem.changeType === ChangeType.ADDED) {
          const deliverable = parentChangeItem.data?.data || parentChangeItem.data
          const components = deliverable?.components?.filter((component) => component.id !== componentId)

          return [
            ChangeRequestActions.modifyDeliverable({
              identity: deliverable?.changeAuditData?.identity || deliverable.id,
              changes: { components: { new: components } },
            }),
          ]
        }

        return [
          ChangeRequestActions.removeChanges({ changeItemUuid: changeItemEntities?.[componentId]?.uuid }),
          ChangeRequestActions.createChanges({
            scopeId: currentScope.identity.id,
            changeRequestId: currentChangeRequest.id,
            payload: {
              changeType: ChangeType.REMOVED,
              type: ChangeItemType.COMPONENT,
              identity: componentId,
              parentId: deliverableId,
            },
          }),
        ]
      })
    )
  )

  revertDeletedComponent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeRequestActions.revertDeletedComponent),
      withLatestFrom(this.store$.select(ChangeRequestSelectors.selectChangeItemEntities)),
      map(([{ componentId }, changeItemEntities]) =>
        ChangeRequestActions.removeChanges({ changeItemUuid: changeItemEntities?.[componentId]?.uuid })
      )
    )
  )
}
